// ============================================================================
// REGION SERVICE
// ============================================================================
// Service layer for the Region Master. getAll transforms backend
// ApiResponse<PagedResult<T>> into frontend PaginatedResponse<T> per the
// mandatory pattern in recsuit-web/CLAUDE.md.

import { get, post, put, del } from '../client/axiosClient';
import { regionEndpoints, REGION_BASE_PATH } from '../endpoints/region.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Region,
  RegionListItem,
  CreateRegionRequest,
  UpdateRegionRequest,
  RegionSearchRequest,
} from '../types/region.types';

class RegionService {
  /** Paginated list. Transforms backend PagedResult to frontend PaginatedResponse. */
  async getAll(params?: RegionSearchRequest): Promise<PaginatedResponse<RegionListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(REGION_BASE_PATH, params)
      : regionEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data; // ApiResponse<PagedResult<RegionDto>>
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Region>> {
    const response = await get<ApiResponse<Region>>(regionEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Region>> {
    const response = await get<ApiResponse<Region>>(regionEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateRegionRequest): Promise<ApiResponse<Region>> {
    const response = await post<ApiResponse<Region>>(regionEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateRegionRequest): Promise<ApiResponse<Region>> {
    const response = await put<ApiResponse<Region>>(regionEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(regionEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${regionEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : regionEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const regionService = new RegionService();
export default regionService;
